import React, { useState } from 'react'
import AdminLayout from '../../../layouts/AdminLayout'
import PaginationComponent from '../components/PaginationComponent'
import SearchComponent from '../components/SearchComponent'
import { useRoles } from '../hooks/useRoles'
import { toast } from 'sonner'
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner'
import FullPageError from '../../../shared/components/errors/FullPageError'
import RolesTable from '../components/RolesTable'
import CreateRoleDialog from '../components/CreateRoleDialog'
import Swal from 'sweetalert2'
import { DeleteRolesSchema } from '../schema/DeleteRolesSchema'
import { useQueryClient } from '@tanstack/react-query'
import { useDeleteRole } from '../hooks/useDeleteRole'
import UpdateRoleDialog from '../components/UpdateRoleDialog'
import AssignUserDialog from '../components/AssignUserDialog'
import AssignPermissionDialog from '../components/AssignPermissionDialog'
import RequireAuth from '../../../auth/middleware/RequireAuth'

const RolesPage: React.FC = () => {
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [showCreate, setShowCreate] = useState<boolean>(false);
    const [showUpdate, setShowUpdate] = useState<boolean>(false);
    const [idWillUpdate, setIdWillUpdate] = useState<number>();
    const [showAssignUser, setShowAssignUser] = useState<boolean>(false);
    const [idWillAssignUser, setIdWillAssignUser] = useState<number>();
    const [showAssignPermission, setShowAssignPermission] = useState<boolean>(false);
    const [idWillAssignPermission, setIdWillAssignPermission] = useState<number>();

    const handleSearch = (query: string) => {
        setSearchQuery(query);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const { data: roles, isSuccess, isError, isLoading, error } = useRoles({
        name: 'roles',
        page: currentPage,
        limit: perPage,
        search: searchQuery,
    });

    if (isError) {
        toast.error(error?.message, {
            id: 'fetch-roles',
        });
    }

    const handleOpenUpdate = (roleId: number) => {
        setShowUpdate(true);
        setIdWillUpdate(roleId);
    };

    const handleCloseUpdate = () => {
        setShowUpdate(false);
        setIdWillUpdate(undefined);
    };

    const handleShowAssignUser = (roleId: number) => {
        setShowAssignUser(true);
        setIdWillAssignUser(roleId);
    };
    const handleCloseAssignUser = () => {
        setShowAssignUser(false);
        setIdWillAssignUser(undefined);
    };

    const handleOpenAssignPermission = (roleId: number) => {
        setShowAssignPermission(true);
        setIdWillAssignPermission(roleId);
    }
    const handleCloseAssignPermission = () => {
        setShowAssignPermission(false);
        setIdWillAssignPermission(undefined);
    }

    const { mutate: deleteRole } = useDeleteRole();

    const queryClient = useQueryClient();

    const handleDelete = (roleId: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: 'You will not be able to recover this role!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading('Deleting role...', {
                    id: 'delete-role',
                });

                const validation = DeleteRolesSchema.safeParse({
                    id: roleId
                });

                if (!validation.success) {
                    toast.error('Invalid role id', {
                        id: 'delete-role',
                    });
                    return;
                }

                deleteRole(validation.data, {
                    onSuccess: async () => {
                        toast.success('Role has been deleted', {
                            id: 'delete-role',
                        });

                        await queryClient.refetchQueries({
                            predicate: (query) => query.queryKey.some((key) => ['roles'].includes(key as string)),
                        });
                    },
                    onError: () => {
                        toast.error('Failed to delete role', {
                            id: 'delete-role',
                        });
                    }
                });
            }
        })
    };

    return (
        <AdminLayout>
            {isError && <FullPageError />}
            {isLoading && <FullPageSpinner />}
            {isSuccess && (
                <div className="container-fluid mt-5">
                    <div className="table-container">
                        <div className="row g-3 align-items-center mb-4">
                            <div className="col-12 col-md-auto">
                                <select
                                    className="form-select form-control"
                                    value={perPage}
                                    onChange={(e) => setPerPage(Number(e.target.value))}
                                >
                                    <option value="10">10</option>
                                    <option value="25">25</option>
                                    <option value="50">50</option>
                                    <option value="100">100</option>
                                </select>
                            </div>
                            <div className="col-12 col-md">
                                <SearchComponent queryKey={searchQuery} onSearch={handleSearch} />
                            </div>
                            <div className="col-12 col-md-auto">
                                {/* Create button */}
                                <RequireAuth requiredRoles={['dev']} requiredPermissions={['role-create']} asComponent>
                                    <button className="btn btn-primary w-100" onClick={() => setShowCreate(true)}>Create</button>
                                </RequireAuth>
                            </div>
                        </div>

                        <div className='table-responsive'>
                            {/* Table */}
                            <RolesTable roles={roles} onUpdate={handleOpenUpdate} onDelete={handleDelete} onAssignUser={handleShowAssignUser} onAssignPermission={handleOpenAssignPermission} />
                        </div>

                        {/* Pagination */}
                        <PaginationComponent
                            totalPages={roles.pagination.last_page}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                        />
                    </div>
                </div>
            )}

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['role-create']} asComponent>
                <CreateRoleDialog show={showCreate} onClose={() => setShowCreate(false)} />
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['role-update']} asComponent>
                <UpdateRoleDialog show={showUpdate} onClose={handleCloseUpdate} roleId={idWillUpdate} />
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['role_assign_user']} asComponent>
                <AssignUserDialog show={showAssignUser} onClose={handleCloseAssignUser} roleId={idWillAssignUser} />
            </RequireAuth>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['role_assign_permission']} asComponent>
                <AssignPermissionDialog show={showAssignPermission} onClose={handleCloseAssignPermission} roleId={idWillAssignPermission} />
            </RequireAuth>
        </AdminLayout>
    )
}

export default RolesPage